import { Table, TableProps, message, Row, Col, Grid } from 'antd';
import { SorterResult } from 'antd/lib/table/interface';
import config from 'config';
import {
  OrderOutput,
  useGetOrdersListQuery,
  OrdersListSort,
  Order,
  OrdersListFilterInput,
} from 'gql/types';
import { Spin, TableSearchInput } from 'modules/common/components';
import { FilterChips } from 'modules/common/components/FilterChips';
import { useDebounce, useFilter, useSort } from 'modules/common/hooks';
import { usePagination } from 'modules/navigation/hooks/usePagination';
import OrderDetails from 'modules/orders/components/OrderDetails';
import {
  createOrdersTableChipsConfig,
  excludedFilterFields,
} from 'modules/orders/configs/ordersTableFilters';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getOrdersTableColumn } from './columns';
import styles from './styles.module.scss';

const { useBreakpoint } = Grid;

const OrdersTable: FC = () => {
  const { t } = useTranslation('orders.OrdersTable');
  const pagination = usePagination();
  const filter = useFilter<OrdersListFilterInput>();
  const sort = useSort<OrdersListSort>();
  const orderFilter = filter.getFilterObject();

  const ordersListQuery = useGetOrdersListQuery({
    variables: {
      input: {
        take: config.PAGINATION.PAGE_SIZE,
        skip: pagination.skip,
        sort: sort.options[0],
        filter: orderFilter,
      },
    },
    onCompleted: (data) => {
      pagination.setTotal(data.getOrdersList.meta.total);
    },
    onError: (error) => {
      message.error(error.message);
    },
  });
  const isLoading = ordersListQuery.loading;
  const ordersList = ordersListQuery.data?.getOrdersList.data as OrderOutput[];

  const chipsConfig = useMemo(() => createOrdersTableChipsConfig(t), [t]);

  const onTableChange: TableProps<OrderOutput>['onChange'] = (
    _,
    filters,
    sorter,
    extra
  ) => {
    if (extra.action === 'sort') {
      const tableSorter = sorter as SorterResult<OrderOutput>;
      const fieldName = tableSorter.columnKey as OrdersListSort;
      const order = tableSorter.order === 'ascend' ? Order.Asc : Order.Desc;
      if (fieldName && order && tableSorter.order) {
        sort.setSortOption({ fieldName, order });
      }
      if (!(sorter as any).order) {
        sort.removeSortOption(fieldName);
      }
    }

    if (extra.action === 'filter') {
      Object.entries(filters).forEach(([fieldName, value]) => {
        filter.setFilterOption({
          fieldName: fieldName as keyof OrdersListFilterInput,
          value,
        });
      });
    }
  };

  const { debounce } = useDebounce();
  const handleSearch = (value: string) => {
    pagination.moveTo(1);
    const number = Number(value);
    const isNumber = !isNaN(number);

    if (isNumber) {
      filter.setFilterOption({
        fieldName: 'number',
        value: number,
      });
    } else {
      filter.removeFilterOption('number');
    }
  };

  const { md } = useBreakpoint();
  const column = getOrdersTableColumn(sort?.options[0], md);

  return (
    <Spin spinning={isLoading}>
      <Row gutter={[0, 16]}>
        <Col span={24}>
          <Col flex={1}>
            <TableSearchInput
              onSearchChange={debounce(handleSearch)}
              placeholder={t('searchByOrderNumber')}
              maxLength={100}
              paramKeys={['number']}
            />
          </Col>
        </Col>

        <Col span={24}>
          <FilterChips<OrdersListFilterInput>
            filterHook={filter}
            config={chipsConfig}
            excludeFields={excludedFilterFields}
          />
        </Col>

        <Col span={24}>
          <Table
            className={styles.table}
            columns={column}
            onChange={onTableChange}
            expandable={{
              expandedRowRender: (order) => <OrderDetails order={order} />,
            }}
            rowKey={({ id }) => id}
            dataSource={ordersList}
            pagination={pagination.config}
          />
        </Col>
      </Row>
    </Spin>
  );
};

export default OrdersTable;
